import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { sectionNames } from "@saleor/intl";
import { SearchCornermarks_search_edges_node } from "@saleor/searches/types/SearchCornermarks";
import { SaleType as SaleTypeE } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import { useIntl } from "react-intl";

import { maybe, splitDateTime } from "../../../misc";
import {
  FetchMoreProps,
  ListProps2,
  ReorderAction,
  TabListActions
} from "../../../types";
import {
  SaleType as SaleTypeEnum,
  SaleTypeEnum as SaleRangeTypeEnum
} from "../../../types/globalTypes";
import { SaleDetails_sale } from "../../types/SaleDetails";
import DiscountCategories from "../DiscountCategories";
import DiscountCollections from "../DiscountCollections";
import DiscountDates from "../DiscountDates";
import DiscountExcludeCategories from "../DiscountExcludeCategories";
import DiscountExcludeCollections from "../DiscountExcludeCollections";
import DiscountExcludeProducts from "../DiscountExcludeProducts";
import DiscountExcludeVariants from "../DiscountExcludeVariants";
// import DiscountProducts from "../DiscountProducts";
import DiscountSaleProducts from "../DiscountSaleProducts";
// import DiscountVariants from "../DiscountVariants";
import DiscountSaleVariants from "../DiscountSaleVariants";
import DiscountSuperPrice from "../DiscountSuperPrice";
import LanguageChoice from "../LanguageChoice/LanguageChoice";
import SaleCornermarks from "../SaleCornermarks";
import SaleInfo from "../SaleInfo";
import SaleSummary from "../SaleSummary";
import SaleType from "../SaleType";
import SaleValue from "../SaleValue";

interface ProductsData {
  productId: string;
  usageLimit: number;
  discountPrice: string;
  sortOrder: any;
}

interface VariantsData {
  variantId: string;
  usageLimit: number;
  discountPrice: string;
  sortOrder: any;
}

export interface SaleDetailsPageFormData {
  endDate: string;
  endTime: string;
  onlyBuyOne: boolean;
  hasEndDate: boolean;
  prescriptionRequired: boolean;
  name: string;
  startDate: string;
  startTime: string;
  type: SaleTypeEnum;
  rangeType: SaleRangeTypeEnum;
  value: string;
  products?: ProductsData[];
  variants?: VariantsData[];
  cornerMark: string;
  showSuperPrice: boolean;
  showOriginalPrice: boolean;
  superName: string;
  superPriceText: string;
  superPriceImage: string;
  superPriceImageMb: string;
}

export enum SaleDetailsPageTab {
  categories = "categories",
  collections = "collections",
  products = "products",
  variants = "variants",
  saleProducts = "saleProducts"
}
export function saleDetailsPageTab(tab: string): SaleDetailsPageTab {
  return tab === SaleDetailsPageTab.variants
    ? SaleDetailsPageTab.variants
    : tab === SaleDetailsPageTab.products
    ? SaleDetailsPageTab.products
    : tab === SaleDetailsPageTab.collections
    ? SaleDetailsPageTab.collections
    : SaleDetailsPageTab.categories;
}

export interface SaleDetailsPageProps
  extends Pick<ListProps2, Exclude<keyof ListProps2, "onRowClick">>,
    TabListActions<
      | "categoryListToolbar"
      | "collectionListToolbar"
      | "productListToolbar"
      | "variantListToolbar"
      | "excludeCategoryListToolbar"
      | "excludeCollectionListToolbar"
      | "excludeProductListToolbar"
      | "excludeVariantListToolbar"
    > {
  activeTab: SaleDetailsPageTab;
  defaultCurrency: string;
  errors: DiscountErrorFragment[];
  sale: SaleDetails_sale;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  cornermarks?: SearchCornermarks_search_edges_node[];
  onLanguageAssign: () => void;
  onLanguageUnAssign: (id: string) => void;
  onCategoryAssign: () => void;
  onCategoryUnassign: (id: string) => void;
  onCategoryClick: (id: string) => () => void;
  onCollectionAssign: () => void;
  onCollectionUnassign: (id: string) => void;
  onCollectionClick: (id: string) => () => void;
  onProductAssign: () => void;
  onProductUnassign: (id: string) => void;
  onProductClick: (id: string) => () => void;
  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
  onExcludeVariantAssign: () => void;
  onExcludeProductAssign: () => void;
  onExcludeCategoryAssign: () => void;
  onExcludeCollectionAssign: () => void;
  onExcludeVariantUnassign: (id: string) => void;
  onExcludeProductUnassign: (id: string) => void;
  onExcludeCategoryUnassign: (id: string) => void;
  onExcludeCollectionUnassign: (id: string) => void;
  onVariantClick: (productId: string, variantId: string) => () => void;
  onRemove: () => void;
  onSubmit: (data: SaleDetailsPageFormData) => void;
  onTabClick: (index: SaleDetailsPageTab) => void;
  onSaleProductAssign: () => void;
  onSaleProductUnassign: (id: string) => void;
  fetchCornermarks?: (data: string) => void;
  fetchMoreCornermarks?: FetchMoreProps;
  onVariantReorder: ReorderAction;
  onProductReorder: ReorderAction;
}

const CategoriesTab = Tab(SaleDetailsPageTab.categories);
const CollectionsTab = Tab(SaleDetailsPageTab.collections);
const ProductsTab = Tab(SaleDetailsPageTab.products);
const VariantsTab = Tab(SaleDetailsPageTab.variants);

const SaleDetailsPage: React.FC<SaleDetailsPageProps> = ({
  activeTab,
  defaultCurrency,
  disabled,
  errors,
  cornermarks: cornermarkChoiceList,
  fetchCornermarks,
  fetchMoreCornermarks,
  onRemove,
  onSubmit,
  onTabClick,
  pageInfo,
  sale,
  saveButtonBarState,
  onBack,
  onLanguageAssign,
  onLanguageUnAssign,
  onCategoryAssign,
  onCategoryUnassign,
  onCategoryClick,
  onCollectionAssign,
  onCollectionUnassign,
  onCollectionClick,
  onExcludeVariantAssign,
  onExcludeProductAssign,
  onExcludeCategoryAssign,
  onExcludeCollectionAssign,
  onExcludeVariantUnassign,
  onExcludeProductUnassign,
  onExcludeCategoryUnassign,
  onExcludeCollectionUnassign,
  onNextPage,
  onPreviousPage,
  // onProductAssign,
  // onProductUnassign,
  onProductClick,
  onVariantAssign,
  onVariantUnassign,
  onVariantClick,
  categoryListToolbar,
  collectionListToolbar,
  productListToolbar,
  variantListToolbar,
  excludeCategoryListToolbar,
  excludeCollectionListToolbar,
  excludeProductListToolbar,
  excludeVariantListToolbar,
  isChecked,
  selected,
  toggle,
  toggleAll,
  onSaleProductAssign,
  onSaleProductUnassign,
  onVariantReorder,
  onProductReorder
}) => {
  const intl = useIntl();

  const initialForm: SaleDetailsPageFormData = {
    showSuperPrice: maybe(() => !!sale.showSuperPrice),
    showOriginalPrice: maybe(() => !!sale.showOriginalPrice),
    onlyBuyOne: maybe(() => !!sale.onlyBuyOne),
    superName: maybe(() => sale.superName, ""),
    superPriceText: maybe(() => sale.superPriceText, ""),
    superPriceImage: maybe(() => sale.superPriceImage, ""),
    superPriceImageMb: maybe(() => sale.superPriceImageMb, ""),
    endDate: splitDateTime(maybe(() => sale.endDate, "")).date,
    endTime: splitDateTime(maybe(() => sale.endDate, "")).time,
    hasEndDate: maybe(() => !!sale.endDate),
    name: maybe(() => sale.name, ""),
    startDate: splitDateTime(maybe(() => sale.startDate, "")).date,
    startTime: splitDateTime(maybe(() => sale.startDate, "")).time,
    type: maybe(() => sale.type, SaleTypeEnum.FIXED),
    rangeType: maybe(
      () =>
        sale.type === SaleTypeEnum.CUSTOM
          ? SaleRangeTypeEnum.SPECIFIC_PRODUCT
          : sale.rangeType,
      SaleRangeTypeEnum.SPECIFIC_PRODUCT
    ),
    value: maybe(() => sale.value.toString(), ""),
    prescriptionRequired: maybe(() => sale.prescriptionRequired, false),
    cornerMark: maybe(() => sale.cornerMark.titleText.toString(), ""),
    products: maybe(() =>
      sale.type === SaleTypeE.CUSTOM
        ? sale.customProducts.map(item => ({
            productId: item.product.id,
            usageLimit: item.usageLimit,
            discountPrice: item.discountPrice.toString(),
            sortOrder: item.sortOrder || null
          }))
        : sale.saleProducts.edges.map(item => ({
            productId: item.node.product.id,
            usageLimit: item.node.usageLimit,
            discountPrice: item.node.discountPrice.toString(),
            sortOrder: item.node.sortOrder || null
          }))
    ),
    variants: maybe(() =>
      sale.type === SaleTypeE.CUSTOM
        ? sale.customVariants.map(item => ({
            variantId: item.variant.id,
            usageLimit: item.usageLimit,
            discountPrice: item.discountPrice.toString(),
            sortOrder: item.sortOrder || null
          }))
        : sale.saleVariants.edges.map(item => ({
            variantId: item.node.variant.id,
            usageLimit: item.node.usageLimit,
            discountPrice: item.node.discountPrice.toString(),
            sortOrder: item.node.sortOrder || null
          }))
    )
  };

  const cornermarks = maybe(() => cornermarkChoiceList, []).map(cornermark => ({
    label: cornermark.titleText,
    value: cornermark
  }));

  const [selectedCornermark, setSelectedCornermark] = useStateFromProps(
    sale?.cornerMark?.titleText || ""
  );

  return (
    <Form initial={initialForm} onSubmit={onSubmit}>
      {({ change, data, hasChanged, setNameValue, submit }) => {
        const handleCornerMarkSelect = createSingleAutocompleteSelectHandler(
          change,
          setSelectedCornermark,
          cornermarks
        );
        return (
          <Container>
            <AppHeader onBack={onBack}>
              {intl.formatMessage(sectionNames.sales)}
            </AppHeader>
            <PageHeader title={maybe(() => sale.name)} />
            <Grid>
              <div>
                <SaleInfo
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                />
                <CardSpacer />
                <SaleType
                  data={data}
                  disabled={disabled}
                  onChange={change}
                  onTabClick={onTabClick}
                />
                <CardSpacer />
                {data.type !== SaleTypeEnum.CUSTOM && (
                  <SaleValue
                    currencySymbol={defaultCurrency}
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    onChange={change}
                  />
                )}
                <CardSpacer />
                {data.rangeType === SaleRangeTypeEnum.SPECIFIC_PRODUCT ? (
                  <>
                    <TabContainer>
                      {SaleTypeEnum.CUSTOM !== data.type ? (
                        <>
                          <CategoriesTab
                            isActive={
                              activeTab === SaleDetailsPageTab.categories
                            }
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage: "Categories ({quantity})",
                                description: "number of categories",
                                id: "saleDetailsPageCategoriesQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.categories.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </CategoriesTab>
                          <CollectionsTab
                            isActive={
                              activeTab === SaleDetailsPageTab.collections
                            }
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage:
                                  "Recommendation Groups ({quantity})",
                                description: "number of collections",
                                id: "saleDetailsPageCollectionsQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.collections.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </CollectionsTab>
                          <ProductsTab
                            isActive={activeTab === SaleDetailsPageTab.products}
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage: "Products ({quantity})",
                                description: "number of products",
                                id: "saleDetailsPageProductsQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.saleProducts.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </ProductsTab>
                          <VariantsTab
                            isActive={activeTab === SaleDetailsPageTab.variants}
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage: "SKU({quantity})",
                                description: "number of skus",
                                id: "saleDetailsPageVariantsQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.variants.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </VariantsTab>
                        </>
                      ) : (
                        <>
                          <ProductsTab
                            isActive={activeTab === SaleDetailsPageTab.products}
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage: "Products ({quantity})",
                                description: "number of products",
                                id: "saleDetailsPageProductsQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.saleProducts.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </ProductsTab>
                          <VariantsTab
                            isActive={activeTab === SaleDetailsPageTab.variants}
                            changeTab={onTabClick}
                          >
                            {intl.formatMessage(
                              {
                                defaultMessage: "SKU({quantity})",
                                description: "number of skus",
                                id: "saleDetailsPageVariantsQuantity"
                              },
                              {
                                quantity: maybe(
                                  () => sale.saleVariants.totalCount.toString(),
                                  "…"
                                )
                              }
                            )}
                          </VariantsTab>
                        </>
                      )}
                    </TabContainer>
                    <CardSpacer />
                    {activeTab === SaleDetailsPageTab.categories ? (
                      <DiscountCategories
                        disabled={disabled}
                        onCategoryAssign={onCategoryAssign}
                        onCategoryUnassign={onCategoryUnassign}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onRowClick={onCategoryClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={categoryListToolbar}
                      />
                    ) : activeTab === SaleDetailsPageTab.collections ? (
                      <DiscountCollections
                        disabled={disabled}
                        onCollectionAssign={onCollectionAssign}
                        onCollectionUnassign={onCollectionUnassign}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onRowClick={onCollectionClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={collectionListToolbar}
                      />
                    ) : activeTab === SaleDetailsPageTab.products ? (
                      // <DiscountProducts
                      // ) : (
                      <DiscountSaleProducts
                        disabled={disabled}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onProductAssign={onSaleProductAssign}
                        onProductUnassign={onSaleProductUnassign}
                        onRowClick={onProductClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={productListToolbar}
                        onChange={change}
                        data={data}
                        onProductReorder={onProductReorder}
                      />
                    ) : (
                      <DiscountSaleVariants
                        disabled={disabled}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onVariantAssign={onVariantAssign}
                        onVariantUnassign={onVariantUnassign}
                        onRowClick={onVariantClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={variantListToolbar}
                        onChange={change}
                        data={data}
                        onVariantReorder={onVariantReorder}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <TabContainer>
                      <CategoriesTab
                        isActive={activeTab === SaleDetailsPageTab.categories}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "Categories ({quantity})",
                            description: "number of categories",
                            id: "saleDetailsPageCategoriesQuantity"
                          },
                          {
                            quantity: maybe(
                              () =>
                                sale.excludeCategories.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </CategoriesTab>
                      <CollectionsTab
                        isActive={activeTab === SaleDetailsPageTab.collections}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage:
                              "Recommendation Groups ({quantity})",
                            description: "number of collections",
                            id: "saleDetailsPageCollectionsQuantity"
                          },
                          {
                            quantity: maybe(
                              () =>
                                sale.excludeCollections.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </CollectionsTab>
                      <ProductsTab
                        isActive={activeTab === SaleDetailsPageTab.products}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "Products ({quantity})",
                            description: "number of products",
                            id: "saleDetailsPageProductsQuantity"
                          },
                          {
                            quantity: maybe(
                              () => sale.excludeProducts.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </ProductsTab>
                      <VariantsTab
                        isActive={activeTab === SaleDetailsPageTab.variants}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage(
                          {
                            defaultMessage: "SKU({quantity})",
                            description: "number of skus",
                            id: "saleDetailsPageVariantsQuantity"
                          },
                          {
                            quantity: maybe(
                              () => sale.excludeVariants.totalCount.toString(),
                              "…"
                            )
                          }
                        )}
                      </VariantsTab>
                    </TabContainer>
                    <CardSpacer />
                    {activeTab === SaleDetailsPageTab.categories ? (
                      <DiscountExcludeCategories
                        disabled={disabled}
                        onCategoryAssign={onExcludeCategoryAssign}
                        onCategoryUnassign={onExcludeCategoryUnassign}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onRowClick={onCategoryClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={excludeCategoryListToolbar}
                      />
                    ) : activeTab === SaleDetailsPageTab.collections ? (
                      <DiscountExcludeCollections
                        disabled={disabled}
                        onCollectionAssign={onExcludeCollectionAssign}
                        onCollectionUnassign={onExcludeCollectionUnassign}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onRowClick={onCollectionClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={excludeCollectionListToolbar}
                      />
                    ) : activeTab === SaleDetailsPageTab.products ? (
                      <DiscountExcludeProducts
                        disabled={disabled}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onProductAssign={onExcludeProductAssign}
                        onProductUnassign={onExcludeProductUnassign}
                        onRowClick={onProductClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={excludeProductListToolbar}
                      />
                    ) : (
                      <DiscountExcludeVariants
                        disabled={disabled}
                        onNextPage={onNextPage}
                        onPreviousPage={onPreviousPage}
                        onVariantAssign={onExcludeVariantAssign}
                        onVariantUnassign={onExcludeVariantUnassign}
                        onRowClick={onVariantClick}
                        pageInfo={pageInfo}
                        discount={sale}
                        isChecked={isChecked}
                        selected={selected}
                        toggle={toggle}
                        toggleAll={toggleAll}
                        toolbar={excludeVariantListToolbar}
                      />
                    )}
                  </>
                )}
                <CardSpacer />
                <DiscountDates
                  data={data}
                  disabled={disabled}
                  defaultCurrency={defaultCurrency}
                  errors={errors}
                  onChange={change}
                />
                <CardSpacer />
                <DiscountSuperPrice
                  data={data}
                  disabled={disabled}
                  defaultCurrency={defaultCurrency}
                  errors={errors}
                  onChange={change}
                  setNameValue={setNameValue}
                />
              </div>
              <div>
                <SaleSummary defaultCurrency={defaultCurrency} sale={sale} />
                <CardSpacer />
                <LanguageChoice
                  disabled={disabled}
                  isChecked={isChecked}
                  selected={selected}
                  toggle={toggle}
                  toggleAll={toggleAll}
                  toolbar={productListToolbar}
                  languageList={sale?.languages}
                  onLanguageAssign={onLanguageAssign}
                  onLanguageUnAssign={onLanguageUnAssign}
                />
                <CardSpacer />
                <SaleCornermarks
                  cornermarks={cornermarks}
                  cornermarkInputDisplayValue={selectedCornermark}
                  data={data}
                  disabled={disabled}
                  fetchCornermarks={fetchCornermarks}
                  fetchMoreCornermarks={fetchMoreCornermarks}
                  onCornermarkChange={handleCornerMarkSelect}
                />
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onDelete={onRemove}
              onSave={submit}
              state={saveButtonBarState}
            />
          </Container>
        );
      }}
    </Form>
  );
};
SaleDetailsPage.displayName = "SaleDetailsPage";
export default SaleDetailsPage;
